import { Box, PopoverTrigger, Tooltip } from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  DataTable,
  Link,
  ProgressBarExpensesReceived,
  ProgressBarOurWorkSpend,
  UserTooltip,
} from "@components/ui-kit";
import { TooltipComplexOurWork } from "@components/widgets/TooltipComplexOurWork";
import { Trans, t } from "@lingui/macro";
import { ProjectOrderByColumnEnum } from "@src/__generated__/urql-graphql";
import { ProjectCell } from "@src/components/widgets/TableCell";
import { can } from "@src/utils/components/permissions";
import { currency, percent } from "@src/utils/formatters";
import cs from "classnames";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import { useContext, useMemo } from "react";
import { ExpenseModel, OurWorkModel, type ProjectModel } from "../models";
import { PageBudgetExceededCtx } from "../PageBudgetExceeded";

type BudgetExceededSubRow = ProjectModel["__subRows"][0];

type BudgetExceededRow =
  | {
      id: string;
      rowType: "project";
      project: ProjectModel;
      __subRows?: BudgetExceededRow[];
    }
  | {
      id: string;
      rowType: "subRow";
      project: ProjectModel;
      subRow: BudgetExceededSubRow;
    };

const isProjectRow = (
  row: BudgetExceededRow,
): row is Extract<BudgetExceededRow, { rowType: "project" }> =>
  row.rowType === "project";

function createColumns(): BodyColumnDef<
  BudgetExceededRow,
  ProjectOrderByColumnEnum
>[] {
  return [
    {
      id: "projectManager",
      meta: { label: t`PM` },
      header: t`PM`,
      cell: ({ row }) =>
        isProjectRow(row.original) ? (
          <UserTooltip user={row.original.project.projectManager}>
            <Avatar
              name={row.original.project.projectManager.full_name}
              src={row.original.project.projectManager.image?.urls.thumbnail}
              colorScheme={
                row.original.project.projectManager.profile?.hex_color
              }
            />
          </UserTooltip>
        ) : null,
    },
    {
      id: "title",
      meta: { label: t`Project` },
      header: t`Project`,
      orderBy: ProjectOrderByColumnEnum.ProjectTitle,
      cell: ({ row }) =>
        isProjectRow(row.original) ? (
          <ProjectCell.Title
            project={row.original.project}
            brand={row.original.project.brand}
            billable={row.original.project.billable}
          />
        ) : (
          <Tooltip label={<div>{row.original.subRow.title}</div>}>
            <div>
              <Link
                href={{
                  pathname: "/projects/detail",
                  query: { id: row.original.subRow.project_id },
                  hash: kebabCase(row.original.subRow.title),
                }}
              >
                {row.original.subRow.title}
              </Link>
            </div>
          </Tooltip>
        ),
    },
    {
      id: "ourWork",
      meta: { label: t`Work` },
      header: t`Work`,
      cell: ({ row }) =>
        !isProjectRow(row.original) &&
        row.original.subRow instanceof OurWorkModel && (
          <div>
            <div className="flex">
              {currency.formatByWorkspace(
                (row.original.subRow.total_budget +
                  row.original.subRow.budget_exceeded)!,
              )}
              {" / "}
              {currency.formatByWorkspace(row.original.subRow.total_budget)}
              <span className="ml-1 text-gray-600">
                ({percent.format(row.original.subRow.spent_budget_percent)})
              </span>
            </div>
            <TooltipComplexOurWork
              timeTracking={{
                percent: +row.original.subRow.spent_budget_percent,
                value: row.original.subRow.spent_budget,
                time: row.original.subRow.spent_time,
              }}
              progress={{
                percent: +row.original.subRow.invoiced_percent,
                value: row.original.subRow.remaining_budget,
                time: row.original.subRow.remaining_time,
              }}
            >
              <PopoverTrigger>
                <Box>
                  <ProgressBarOurWorkSpend
                    credit={row.original.subRow.credit}
                    total_budget={row.original.subRow.total_budget}
                    spent_budget={row.original.subRow.spent_budget}
                    isTimeAndMaterials={
                      row.original.subRow.is_time_and_materials
                    }
                  />
                </Box>
              </PopoverTrigger>
            </TooltipComplexOurWork>
          </div>
        ),
    },
    {
      id: "expenses",
      meta: { label: t`Expenses` },
      header: t`Expenses`,
      cell: ({ row }) =>
        !isProjectRow(row.original) &&
        row.original.subRow instanceof ExpenseModel && (
          <div>
            <div className="flex">
              {currency.formatByWorkspace(
                (row.original.subRow.total_budget +
                  row.original.subRow.budget_exceeded)!,
              )}
              {" / "}
              {currency.formatByWorkspace(row.original.subRow.total_budget)}
              <span className="ml-1 text-gray-600">
                ({percent.format(row.original.subRow.real_expenses_percent)})
              </span>
            </div>
            {can("expense_read_own") ||
            can("expense_read_all") ||
            can("expense_read_team") ? (
              <ProgressBarExpensesReceived stats={row.original.subRow.stats} />
            ) : null}
          </div>
        ),
    },
    {
      id: "budgetExceeded",
      meta: { label: t`Budget Exceeded`, align: "center" },
      header: t`Budget Exceeded`,
      orderBy: ProjectOrderByColumnEnum.BudgetExceeded,
      cell: ({ row }) =>
        isProjectRow(row.original) ? (
          <Tooltip
            label={
              <div className="flex flex-col items-center p-4 justify-center text-white text-lg">
                <h4 className="font-bold mb-1">
                  <Trans>Budget Exceeded</Trans>
                </h4>
                {row.original.project.__subRows.map((item) => (
                  <div key={item.id} className="mb-1">
                    <span>{item.title}: </span>
                    <span>
                      {currency.formatByWorkspace(item.budget_exceeded)}
                    </span>
                  </div>
                ))}
              </div>
            }
          >
            <span
              className={cs({
                "text-red": row.original.project.budgetExceeded !== 0,
              })}
            >
              {currency.formatByWorkspace(row.original.project.budgetExceeded)}
            </span>
          </Tooltip>
        ) : (
          <span
            className={cs({
              "text-red": row.original.subRow.budget_exceeded !== 0,
            })}
          >
            {currency.formatByWorkspace(row.original.subRow.budget_exceeded)}
          </span>
        ),
    },
    {
      id: ACTIONS_COLUMN_ID,
    },
  ];
}

export const TableBudgetExceeded = observer(function TableBudgetExceeded() {
  const store = useContext(PageBudgetExceededCtx);
  const cols = useMemo(() => createColumns(), []);
  const data = useMemo<BudgetExceededRow[]>(
    () =>
      store.projects.map((project) => ({
        id: project.id,
        rowType: "project",
        project,
        __subRows: project.__subRows.map((subRow) => ({
          id: subRow.id,
          rowType: "subRow",
          project,
          subRow,
        })),
      })),
    [store.projects],
  );

  return (
    <DataTable.Body<BudgetExceededRow, ProjectOrderByColumnEnum>
      tableKey="budget-exceeded"
      data={data}
      columns={cols}
      orderedBy={store.orderBy.clauses[0]}
      onOrderByChange={(clause) => {
        if (!clause.order) {
          store.orderBy.setClauses([]);
          return;
        }

        store.orderBy.setClauses([
          { column: clause.column, order: clause.order },
        ]);
      }}
    />
  );
});
